import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Slot group definition ────────────────────────────────────────────────────

const imageDef: SlotGroupDefinition = {
  label: 'Section Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
      suggestedFields: ['property_featured_image'],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Section image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a strong, representative hero photo of this property — an exterior, lobby, or signature space that sets the tone.',
    context:
      'Lead image at the top of the Contact page blurb, paired with two smaller photos below. Each of the three images should highlight a different part of the property.',
    sourceCollection: 'photo_gallery',
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Photo 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a photo highlighting a distinct part of the property — an amenity, interior, or grounds shot with a real image.',
    context:
      'Second of three property photos in the Contact page blurb. Each of the three images should showcase a different area, so avoid the hero photo and the third photo.',
    sourceCollection: 'photo_gallery',
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Photo 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Photo',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a photo highlighting another distinct part of the property — an amenity, interior, or grounds shot with a real image.',
    context:
      'Third of three property photos in the Contact page blurb. Each of the three images should showcase a different area, so avoid the hero photo and the second photo.',
    sourceCollection: 'photo_gallery',
  },
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface PageBlurbProps {
  /** Property field that supplies the default body copy (e.g. property_neighborhood_blurb). */
  blurbField?: string;
  /** Unique per-instance prefix so each page stores edits independently. */
  tokenPrefix?: string;
  /** Static fallback body text when neither the override nor the property field has content. */
  defaultText?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PageBlurb({ blurbField, tokenPrefix, defaultText }: PageBlurbProps) {
  // Fallback to the canonical slot name when used standalone (tests/preview).
  const prefix = tokenPrefix || 'pageblurb';
  const eyebrowPath = `${prefix}.eyebrow`;
  const headingPath = `${prefix}.heading`;
  const textPath = `${prefix}.text`;
  const imagePath = `${prefix}.image`;
  const photo2Path = `${prefix}.photo_2`;
  const photo3Path = `${prefix}.photo_3`;

  const eyebrow = useContentToken(eyebrowPath);
  const image = useSlotGroup(imagePath, imageDef);
  const photo2 = useSlotGroup(photo2Path, photo2Def);
  const photo3 = useSlotGroup(photo3Path, photo3Def);

  return (
    <section className="bg-white py-16 md:py-24 overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-10 md:gap-12 lg:gap-20">

        {/* Left column: text content + photo_2 stacked directly beneath it.
            Left-padded to align with where max-w-7xl sits. */}
        <div className="flex flex-col gap-10 md:gap-14 px-4 sm:px-6 lg:px-0 lg:pl-[max(2rem,calc((100vw-80rem)/2+2rem))]">
          <div>
            <EditableToken
              path={eyebrowPath}
              defaultValue="Welcome"
              as="p"
              className="font-body text-base font-semibold text-[var(--vibe-color-text)] mb-8"
            >
              {eyebrow}
            </EditableToken>

            <RichTextContent
              path={headingPath}
              label="Heading"
              defaultValue="<p>Discover the Neighborhood</p>"
              as="h2"
              className="font-heading text-4xl sm:text-5xl font-normal text-[var(--vibe-color-text)] mb-8 leading-[1.1]"
            />

            <RichTextContent
              path={textPath}
              label="Body"
              defaultField={blurbField}
              defaultValue={defaultText || ''}
              as="div"
              className="page-blurb-body font-body text-base text-[var(--vibe-color-text)] leading-relaxed max-w-xl"
            />
          </div>

          {(photo2.image || photo3.image) && (
            <EditableSlotGroup
              groupPath={photo2Path}
              definition={photo2Def}
              className="block"
            >
              {photo2.image ? (
                <VibesitesImage
                  url={photo2.image}
                  alt={photo2.alt || ''}
                  className="w-full rounded-lg object-cover"
                  style={{ aspectRatio: '1/1' }}
                />
              ) : (
                <div
                  className="w-full rounded-lg bg-[var(--vibe-color-surface)]"
                  style={{ aspectRatio: '1/1' }}
                />
              )}
            </EditableSlotGroup>
          )}
        </div>

        {/* Right column: hero (bleeds to the right edge) + photo_3 beneath it
            (inset on the right to mirror the left column's padding). */}
        <div className="flex flex-col gap-10 md:gap-14">
          <EditableSlotGroup
            groupPath={imagePath}
            definition={imageDef}
            className="block px-4 sm:px-6 md:px-0"
          >
            {image.image ? (
              <VibesitesImage
                url={image.image}
                alt={image.alt || ''}
                className="w-full rounded-lg object-cover"
                style={{ aspectRatio: '4/3' }}
              />
            ) : (
              <div
                className="w-full rounded-lg bg-[var(--vibe-color-surface)]"
                style={{ aspectRatio: '4/3' }}
              />
            )}
          </EditableSlotGroup>

          {(photo2.image || photo3.image) && (
            <EditableSlotGroup
              groupPath={photo3Path}
              definition={photo3Def}
              className="block px-4 sm:px-6 md:px-0 lg:pr-[max(2rem,calc((100vw-80rem)/2+2rem))] md:-translate-x-8"
            >
              {photo3.image ? (
                <VibesitesImage
                  url={photo3.image}
                  alt={photo3.alt || ''}
                  className="w-full rounded-lg object-cover"
                  style={{ aspectRatio: '1/1' }}
                />
              ) : (
                <div
                  className="w-full rounded-lg bg-[var(--vibe-color-surface)]"
                  style={{ aspectRatio: '1/1' }}
                />
              )}
            </EditableSlotGroup>
          )}
        </div>
      </div>

      {/* ─── Hidden schema-lint anchors ─────────────────────────────────────── */}
      {/* These never render visibly; they exist solely so the source-level    */}
      {/* linter finds the canonical literal paths for every schema key.       */}
      <EditableToken
        path="pageblurb.eyebrow"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
      <RichTextContent
        path="pageblurb.heading"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="pageblurb.text"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="pageblurb.image"
        definition={imageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="pageblurb.photo_2"
        definition={photo2Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="pageblurb.photo_3"
        definition={photo3Def}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .page-blurb-body p { margin-bottom: 1rem; }
        .page-blurb-body p:last-child { margin-bottom: 0; }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.eyebrow': {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Welcome',
    },
    'pageblurb.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Discover the Neighborhood</p>',
    },
    'pageblurb.text': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_long_description',
      suggestedFields: [
        'property_floorplans_blurb',
        'property_amenities_blurb',
        'property_neighborhood_blurb',
        'property_gallery_blurb',
        'property_scheduletour_blurb',
        'property_interactivemap_blurb',
        'property_pets_blurb',
        'property_virtualtours_blurb',
        'property_contactus_blurb',
        'property_residentaccess_blurb',
        'property_reviews_blurb',
        'property_applynow_blurb',
      ],
      defaultValue: '',
    },
  },
  slotGroups: {
    'pageblurb.image': imageDef,
    'pageblurb.photo_2': photo2Def,
    'pageblurb.photo_3': photo3Def,
  },
};

(PageBlurb as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PageBlurb;
